import React from 'react';
import ReactDOM from 'react-dom/client';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Import Mantine core styles
import '@mantine/core/styles.css';
// Import Mantine DataTable styles
import 'mantine-datatable/styles.css';
// Import Mantine Notifications styles
import '@mantine/notifications/styles.css';
// Import global styles (mobile-friendly base styles)
import '@/styles/global.css';

// Import core library configuration
import '@/lib/env'; // Import for side effects (validation)
import { initSentry } from '@/lib/sentry';
import { queryClient } from '@/lib/query-client';
import { ColorSchemeProvider } from '@/lib/color-scheme';
import { createRouterContext } from '@/lib/router-context';
import { AuthProvider } from '@/shared/contexts/AuthContext';
import { lazyRoute, lazyIndexRoute } from '@/lib/router-utils';
import { NotFoundError } from '@/shared/components/ErrorBoundary';

/**
 * Application Entry Point
 *
 * This file initializes the MailGobbler frontend application.
 */

// Initialize Sentry before rendering
initSentry();

/**
 * Router configuration
 *
 * Uses React Router v7 in SPA mode with client-side routing.
 * Routes are lazy-loaded using the lazyRoute helper to improve bundle size.
 */
const router = createBrowserRouter(
  [
    lazyRoute('/', () => import('@/routes/root'), [
      lazyIndexRoute(() => import('@/routes/index')),
      lazyRoute('feed/:readingId', () => import('@/routes/feed.$readingId')),
      lazyRoute('collection/:readingId', () => import('@/routes/collection.$readingId')),
      lazyRoute('login', () => import('@/routes/login')),
      lazyRoute('register', () => import('@/routes/register')),
      lazyRoute('pending', () => import('@/routes/pending')),
      lazyRoute('email/:readingId', () => import('@/routes/email.$readingId')),
      lazyRoute('app', () => import('@/routes/app'), [
        // Auth Layout
        lazyRoute(undefined, () => import('@/routes/_layout'), [
          lazyRoute('overview', () => import('@/routes/overview')),
          lazyRoute('feeds', () => import('@/routes/feeds')),
          lazyRoute('collections', () => import('@/routes/collections')),
          lazyRoute('feed/:id', () => import('@/routes/feed.$id')),
          lazyRoute('feed/:id/email/:emailId', () => import('@/routes/feed.$id.email.$emailId')),
          lazyRoute('collection/:id', () => import('@/routes/collection.$id')),
          lazyRoute(
            'collection/:id/email/:emailId',
            () => import('@/routes/collection.$id.email.$emailId')
          ),
          lazyRoute('admin', () => import('@/routes/admin/_layout'), [
            lazyIndexRoute(() => import('@/routes/admin/index')),
            lazyRoute('users', () => import('@/routes/admin/users')),
          ]),
        ]),
      ]),
      {
        path: '*',
        Component: NotFoundError,
      },
    ]),
  ],
  {
    getContext: createRouterContext,
  }
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ColorSchemeProvider>
        <Notifications />
        <ModalsProvider>
          <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </Sentry.ErrorBoundary>
        </ModalsProvider>
      </ColorSchemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
